import { Epsilon, Matrix, Ray, Scalar, Vector3 } from '@babylonjs/core';

const Logging = false;

class Cluster {
  public readonly initialCentroid;
  public idxs = new Array<number>();
  public size;

  constructor(
    public idx: number,
    public centroid: Vector3
  ) {
    this.initialCentroid = centroid.clone();
    this.size = centroid.lengthSquared();
  }

  public calcCentroid(vertices: Vector3[], normals: Vector3[]) {
    if (this.idxs.length === 0) {
      this.centroid = this.initialCentroid;
      this.size = 0;
      return;
    }

    this.centroid = this.idxs
      .reduce((acc, idx) => {
        const normal = normals[idx];
        return normal ? acc.addInPlace(normal) : acc;
      }, Vector3.Zero())
      .normalize();
    this.size = this.idxs.reduce((acc, idx) => {
      const normal = normals[idx];
      return normal ? acc + this.centroid.cosSimilarity(normal) : acc;
    }, 0);
  }

  public remove(idx: number) {
    const index = this.idxs.indexOf(idx);
    if (index !== -1) {
      this.idxs.splice(index, 1);
    }
  }

  public getEdge(vertices: Vector3[], normals: Vector3[], offset: number, height: number) {
    let longestEdge: Edge = {
      length: 0,
      v1: Vector3.ZeroReadOnly,
      n1: Vector3.ZeroReadOnly,
      v2: Vector3.ZeroReadOnly,
      n2: Vector3.ZeroReadOnly,
    };

    // 가장 긴 줄 (+= 3 인 이유는 점이 3개씩 같은 노말이기 때문 = mesh vertex 구성)
    for (let i = 0; i < this.idxs.length; i += 3) {
      const idx0 = this.idxs[i];
      const idx1 = this.idxs[i + 1];
      if (!idx0 || !idx1) {
        console.error(`Invalid index ${i}`);
        return;
      }
      const v1 = vertices[idx0];
      if (!v1) {
        console.error(`Invalid index ${idx0}`);
        return;
      }
      if (i + 1 >= this.idxs.length || idx1 >= vertices.length || idx1 >= normals.length) {
        console.error(`Invalid index ${i + 1}`);
        return;
      }
      const v2 = vertices[idx1];
      if (!v2) {
        console.error(`Invalid index ${idx1}`);
        return;
      }
      const length = Vector3.Distance(v1, v2);
      if (longestEdge.length < length) {
        longestEdge.length = length;
        longestEdge.v1 = v1;
        longestEdge.v2 = v2;
        const n1 = normals[idx0];
        const n2 = normals[idx1];
        if (!n1 || !n2) {
          console.error(`Invalid normal ${idx0} ${idx1}`);
          return;
        }
        longestEdge.n1 = n1;
        longestEdge.n2 = n2;
      }
      if (Logging) {
        console.debug(`Cluster(${this.idx}) (${idx0}, ${idx1}): ${length}`);
      }
    }

    // 모든 점을 가장 긴 선에 프로젝션
    let projected = new Map<number, Vector3>();
    let projectedNorm: Vector3[] = [];
    let cnt = 0;
    for (let i = 0; i < this.idxs.length; i += 3) {
      const idx0 = this.idxs[i];
      const idx1 = this.idxs[i + 1];
      if (!idx0 || !idx1) {
        console.error(`Invalid index ${i}`);
        return;
      }
      let v1 = vertices[idx0];
      let n1 = normals[idx0];
      if (!v1 || !n1) {
        console.error(`Invalid index ${idx0}`);
        return;
      }
      let pt1 = projectOntoNormal(v1.subtract(longestEdge.v1), longestEdge.v2.subtract(longestEdge.v1)).add(
        longestEdge.v1
      );
      // 너무 멀리 있거나 너무 다른 녀석들 제거
      if (longestEdge.n1.cosSimilarity(n1) > 0.75 && Vector3.Distance(v1, pt1) < longestEdge.length) {
        projected.set(cnt++, pt1);
        projectedNorm.push(n1);
      }
      let v2 = vertices[idx1];
      let n2 = normals[idx1];
      if (!v2 || !n2) {
        console.error(`Invalid index ${idx1}`);
        return;
      }
      let pt2 = projectOntoNormal(v2.subtract(longestEdge.v1), longestEdge.v2.subtract(longestEdge.v1)).add(
        longestEdge.v1
      );
      if (longestEdge.n1.cosSimilarity(n2) > 0.75 && Vector3.Distance(v2, pt2) < longestEdge.length) {
        projected.set(cnt++, pt2);
        projectedNorm.push(n2);
      }
    }
    let lengthMin = 1.5;
    // 그 중에서 가장 긴 선분 찾기
    let projectedEdge: Edge = {
      length: 0,
      v1: Vector3.ZeroReadOnly,
      n1: Vector3.ZeroReadOnly,
      v2: Vector3.ZeroReadOnly,
      n2: Vector3.ZeroReadOnly,
    };
    let xOffset = Math.abs(longestEdge.v2.x - longestEdge.v1.x);
    let zOffset = Math.abs(longestEdge.v2.z - longestEdge.v1.z);
    const projectedEntries = [...projected.entries()];
    let sorted =
      xOffset > zOffset
        ? projectedEntries.sort((a, b) => a[1].x - b[1].x)
        : projectedEntries.sort((a, b) => a[1].z - b[1].z);
    for (let i = 0; i < sorted.length - 1; i++) {
      const p0 = sorted[i];
      const p1 = sorted[i + 1];
      if (!p0 || !p1) {
        console.error(`Invalid index ${i}`);
        return;
      }
      let v1 = p0[1];
      let v2 = p1[1];
      let length = Vector3.Distance(v1, v2);
      if (Logging) {
        console.debug(`Cluster(${this.idx}) projected (${this.idxs[p0[0]]}, ${this.idxs[p1[0]]}): ${length}`);
      }
      if (projectedEdge.length <= length) {
        projectedEdge.length = length;
        projectedEdge.v1 = v1;
        projectedEdge.v2 = v2;
        let n1 = projectedNorm[p0[0]];
        let n2 = projectedNorm[p1[0]];
        if (!n1 || !n2) {
          console.error(`Invalid normal ${p0[0]} ${p1[0]}`);
          return;
        }
        projectedEdge.n1 = n1;
        projectedEdge.n2 = n2;
      }
    }

    if (projectedEdge.length < lengthMin) {
      return;
    }
    // 그 선분을 Centroid 에서 offset 및 height 만큼 옮기기
    const offsetVector = this.centroid.scale(offset).addInPlaceFromFloats(0, height, 0);
    projectedEdge.v1.addInPlace(offsetVector);
    projectedEdge.v2.addInPlace(offsetVector);
    return projectedEdge;
  }
}

export class KMeansCluster {
  private readonly clusterCount = 4;
  public clusters: Cluster[] = [];
  private static Preset1 = [
    new Cluster(0, Vector3.LeftReadOnly),
    new Cluster(1, Vector3.RightReadOnly),
    new Cluster(2, Vector3.LeftHandedForwardReadOnly),
    new Cluster(3, Vector3.LeftHandedBackwardReadOnly),
  ];
  private static Preset2 = [
    new Cluster(0, new Vector3(-0.5, 0, 0.5).normalize()),
    new Cluster(1, new Vector3(0.5, 0, -0.5).normalize()),
    new Cluster(2, new Vector3(0.5, 0, 0.5).normalize()),
    new Cluster(3, new Vector3(-0.5, 0, -0.5).normalize()),
  ];

  constructor(vertices: Vector3[], normals: Vector3[], pts: number[], rotationMatrix: Matrix) {
    this.init(KMeansCluster.Preset1, vertices, normals, pts, rotationMatrix);
    this.doClustering(vertices, normals, pts);
    if (this.clusters.some((t) => t.idxs.length == 0)) {
      this.init(KMeansCluster.Preset2, vertices, normals, pts, rotationMatrix);
      this.doClustering(vertices, normals, pts);
    }
  }

  private init(preset: Cluster[], vertices: Vector3[], normals: Vector3[], pts: number[], rotationMatrix: Matrix) {
    this.clusters = preset.map((t) => new Cluster(t.idx, Vector3.TransformCoordinates(t.centroid, rotationMatrix)));
    // Initialize
    for (let j = 0; j < pts.length; j++) {
      const idx = pts[j];
      if (!idx) {
        console.error(`Invalid index ${j}`);
        return;
      }
      const targetNormal = normals[idx];
      if (!targetNormal) {
        console.error(`Invalid normal ${idx}`);
        return;
      }
      let maxSimilarity = -Infinity;
      let select = -1;

      for (let i = 0; i < this.clusterCount; i++) {
        const centroid = this.clusters[i]?.centroid;
        if (!centroid) {
          console.error(`Invalid centroid ${i}`);
          return;
        }
        const similarity = centroid.cosSimilarity(targetNormal);
        if (similarity > maxSimilarity) {
          maxSimilarity = similarity;
          select = i;
        }
      }

      if (Logging) {
        console.debug(`pt ${idx} select ${select}`);
      }

      this.clusters[select]?.idxs.push(idx);
    }

    for (let cluster of this.clusters) {
      cluster.calcCentroid(vertices, normals);
      if (Logging) {
        console.debug(`Cluster[${cluster.idx}]: Size: ${cluster.size} pts: ${cluster.idxs.join(',')}`);
      }
    }
  }

  private doClustering(vertices: Vector3[], normals: Vector3[], pts: number[]) {
    let done = true;
    for (let i = 0; i < 10; i++) {
      const prevSize = this.clusters.map((t) => t.size);
      for (let j = 0; j < pts.length; j++) {
        const ptsj = pts[j];
        if (!ptsj) {
          console.error(`Invalid index ${j}`);
          return;
        }
        let prevClusterIdx = -1;
        for (let c = 0; c < this.clusters.length; c++) {
          const cluster = this.clusters[c];
          if (!cluster) {
            console.error(`Invalid cluster ${c}`);
            return;
          }
          const idx = cluster.idxs.findIndex((t) => t === ptsj);
          if (idx !== -1) {
            prevClusterIdx = c;
            cluster.remove(ptsj);
            break;
          }
        }
        console.assert(prevClusterIdx !== -1);
        const normal = normals[ptsj];
        const centroid = this.clusters[prevClusterIdx]?.centroid;
        if (!normal || !centroid) {
          console.error(`Invalid normal ${ptsj}`);
          return;
        }
        const prefSimilarity = centroid.cosSimilarity(normal);
        // select cluster
        let select = [...Array(this.clusterCount).keys()].reduce((a, b) => {
          const centroidA = this.clusters[a]?.centroid;
          const centroidB = this.clusters[b]?.centroid;
          if (!centroidA || !centroidB) {
            console.error(`Invalid centroid ${a} ${b}`);
            return a;
          }
          return centroidA.cosSimilarity(normal) > centroidB.cosSimilarity(normal) ? a : b;
        });
        const selectCentroid = this.clusters[select]?.centroid;
        if (!selectCentroid) {
          console.error(`Invalid centroid ${select}`);
          return;
        }
        if (selectCentroid.cosSimilarity(normal) < prefSimilarity) {
          if (Logging) {
            console.debug(`pt ${j} newly selected ${select} ignored because cossimilarity smaller than prev`);
          }
          this.clusters[prevClusterIdx]?.idxs.push(ptsj);
        } else {
          this.clusters[select]?.idxs.push(ptsj);
        }
      }
      for (let cluster of this.clusters) {
        cluster.calcCentroid(vertices, normals);
        if (Logging) {
          console.debug(`Loop${i} Cluster[${cluster.idx}]: Size: ${cluster.size} pts: ${cluster.idxs.join(',')}`);
        }
      }
      const curSize = this.clusters.map((t) => t.size);
      done = true;
      for (let k = 0; k < this.clusterCount; k++) {
        if (!Scalar.WithinEpsilon(prevSize[k] ?? NaN, curSize[k] ?? NaN, Epsilon)) {
          // console.debug(prevSize[k], curSize[k]);
          done = false;
        }
      }
      if (done) {
        if (Logging) {
          console.debug('Clustering Done.');
        }
        break;
      }
    }
    if (!done) {
      console.warn('Clustering Failed');
    }
  }
}

export class KMeansClusterInstance {
  public edges: (Edge | undefined)[] = [];

  public enable = [true, true, true, true];
  private rays = [] as Ray[];

  public get clusters() {
    return this.kMeans.clusters;
  }

  constructor(
    private kMeans: KMeansCluster,
    private position: Vector3
  ) {}

  update(hoName: string, clusterIdx: number, hoCenter: Vector3) {
    const cluster = this.kMeans.clusters[clusterIdx];
    const edge = this.edges[clusterIdx];
    if (!cluster || !edge) {
      console.error(`Invalid cluster ${clusterIdx} or edge ${edge}`);
      return;
    }
    const center = edge.v1.add(edge.v2).scale(0.5);
    this.enable[clusterIdx] =
      (this.enable[clusterIdx] &&
        this.checkHit(clusterIdx, 0, hoName, center, cluster.centroid) &&
        this.checkHit(clusterIdx, 1, hoName, hoCenter.subtract(this.position), cluster.centroid)) ??
      false;
    if (this.enable[clusterIdx]) {
      let v1 = edge.v1;
      let v2 = edge.v2;
      if (!this.checkHit(clusterIdx, 2, hoName, edge.v1.scale(0.25).add(edge.v2.scale(0.75)), edge.n2)) {
        v2 = edge.v1.scale(0.25).add(edge.v2.scale(0.75));
      }
      if (!this.checkHit(clusterIdx, 3, hoName, edge.v1.scale(0.75).add(edge.v2.scale(0.25)), edge.n1)) {
        v1 = edge.v1.scale(0.75).add(edge.v2.scale(0.25));
      }
      edge.v1 = v1;
      edge.v2 = v2;
      edge.length = Vector3.Distance(v1, v2);
    } else {
      this.edges[clusterIdx] = undefined;
    }
  }

  /**
   * @return 간섭되는 다른 호가 없으면 `true`
   */
  private checkHit(clusterIdx: number, rayIdx: number, hoName: string, origin: Vector3, dir: Vector3): boolean {
    // FIXME: ray 비용이 너무 커서 생략
    return true;
    // const ray = new Ray(this.position.add(origin).subtract(dir), dir, 5);
    // this.rays[clusterIdx * 4 + rayIdx] = ray;
    // const hit = Engine.LastCreatedScene?.pickWithRay(
    //   ray,
    //   (m) => Tags.MatchesQuery(m, 'unit') && m.name !== hoName,
    //   true
    // );

    // if (Logging && hit?.pickedMesh) {
    //   const ho = hit.pickedMesh;
    //   console.debug(`${clusterIdx}:${rayIdx} {dongName}-${hoName} - hit ${ho.name} distance: ${hit.distance}`);
    // }

    // return !hit?.pickedMesh;
  }
}

export type Edge = {
  length: number;
  v1: Vector3;
  n1: Vector3;
  v2: Vector3;
  n2: Vector3;
};

function projectOntoNormal(vector: Vector3, onNormal: Vector3) {
  let num = Vector3.Dot(onNormal, onNormal);
  if (num < Epsilon) {
    return Vector3.ZeroReadOnly;
  }

  let num2 = Vector3.Dot(vector, onNormal);
  return new Vector3((onNormal.x * num2) / num, (onNormal.y * num2) / num, (onNormal.z * num2) / num);
}
